import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import {
  Grid,
  IconButton,
  Paper as MuiPaper,
  Typography,
  Button,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import PowerOff from '@material-ui/icons/PowerOff';
import { isEmpty } from 'lodash';
import { bool, func, number, shape, string } from 'prop-types';

import { RefreshCounter } from '../../../../../components';
import Table from './table';
import Visualization from './visualization';
import Footer from './footer';
import { getI18n } from '../../../../../utils/intl';
import { KEY_STORE_DISCONNECTED, BULK_DEPLOYMENT_STATUS_RUNNING } from '../../../../../constants';
import { getDeploymentTypeLabel } from "../../../../../utils/deployment";

export const ToggleSubtotal = ({
 classes = {},
 hideSubtotal,
 setHideSubtotal,
}) => {
  const intl = getI18n(useIntl());
  return (
    <IconButton
      className={classes.hideSubtotal}
      id="hide-subtotal" data-layer7-test="hide-subtotal"
      onClick={() => setHideSubtotal(!hideSubtotal)}
    >
      <Typography variant="body1">
        {hideSubtotal ?
          intl.getI18nMessage('label.proxy.details.api.show.subtotal') :
          intl.getI18nMessage('label.proxy.details.api.hide.subtotal')
        }
      </Typography>
    </IconButton>
  );
}

ToggleSubtotal.propTypes = {
  classes: shape({}),
  hideSubtotal: bool,
  setHideSubtotal: func,
};

export const PaperWithStatus = (props) => {
  const {
    classes = {},
    id,
    title,
    deploymentType,
    status,
    errorCount = 0,
    lastUpdated,
    showBreakdown,
    deploymentTypeTooltip,
    refreshTime,
    checkStatus,
    onDeployment,
    bulkDeploymentStatus,
  } = props;

  const [hideSubtotal, setHideSubtotal] = useState(false);
  const intl = getI18n(useIntl());

  const isDisconnected = (status === KEY_STORE_DISCONNECTED);
  const isBulkDeploymentStatusRunning =
  (!isEmpty(bulkDeploymentStatus) ? bulkDeploymentStatus.includes(
    BULK_DEPLOYMENT_STATUS_RUNNING) : false);
  const containerClass = isDisconnected ?
    cx(classes.paper, classes.disconnectedBackground) : classes.paper;

  return (
    <MuiPaper
      square elevation={0}
      className={containerClass}
      id={`${id}-status`}
      data-layer7-test={`${id}-status`}
      data-apim-test={`${id}-status`}
    >
      <Grid container>
        <Grid item>
          <Typography
            variant="h2"
            className={classes.title}
            id={`${id}-status-title`}
            data-layer7-test={`${id}-status-title`}
            data-apim-test={`${id}-status-title`}
          >
            {title}
          </Typography>
        </Grid>
        {isDisconnected &&
          <Grid item>
            <IconButton
              className={classes.disconnectedIndicatorIcon}
              data-disconnected
            >
              <PowerOff />
            </IconButton>
          </Grid>
        }
        {errorCount > 0 &&
          <Grid item>
            <IconButton
              className={classes.errorIndicatorIcon}
              data-error
            >
              <WarningIcon />
            </IconButton>
          </Grid>
        }
        {!isDisconnected && isBulkDeploymentStatusRunning &&
          <Grid item>
            <RefreshCounter
              refreshTime={refreshTime}
              checkStatus={checkStatus}
            />
          </Grid>
        }
      </Grid>
      <Visualization
        {...props}
        isProxyDisconnected={isDisconnected}
      />
      <Table
        {...props}
        hideSubtotal={hideSubtotal}
        isProxyDisconnected={isDisconnected}
      />
      {showBreakdown &&
        <ToggleSubtotal
          classes={classes}
          hideSubtotal={hideSubtotal}
          setHideSubtotal={setHideSubtotal}
        />
      }
      <div className={classes.saveContainer}>
        {deploymentType === 'AUTOMATIC' &&
          <>
            <Button
              id="redeploy-all-button"
              data-apim-test="redeploy-all-button"
              variant="outlined"
              color="secondary"
              onClick={() => onDeployment(true)}
              disabled={isDisconnected || isBulkDeploymentStatusRunning}
              className={classes.deployButton}
            >
              {intl.getI18nMessage('label.key.store.redeploy.all.keys')}
            </Button>
            <Button
              id="redeploy-all-error-button"
              data-apim-test="redeploy-all-error-button"
              variant="outlined"
              color="secondary"
              onClick={() => onDeployment(false)}
              disabled={isDisconnected || isBulkDeploymentStatusRunning}
              className={classes.deployButton}
            >
              {intl.getI18nMessage('label.key.store.redeploy.all.error')}
            </Button>
          </>
        }
      </div>
      <Footer
        id={id}
        deploymentType={getDeploymentTypeLabel(deploymentType)}
        tooltip={deploymentTypeTooltip}
        lastUpdated={lastUpdated}
      />
    </MuiPaper>
  );
}

PaperWithStatus.propTypes = {
  classes: shape({}),
  id: string,
  title: string,
  deploymentType: string,
  deploymentTypeTooltip: shape({}),
  status: string,
  errorCount: number,
  lastUpdated: string,
  showBreakdown: bool,
  refreshTime: func,
  checkStatus: func,
  onDeployment: func,
  bulkDeploymentStatus: string,
};
PaperWithStatus.displayName = 'PaperWithStatus';

export default PaperWithStatus;
